import React from 'react';
import { X, AlertTriangle, TrendingUp, Check } from '../../grademind/Icons';

const PlanLimitModal = ({ isOpen, onClose, limitInfo, onUpgrade }) => {
    if (!isOpen) return null;

    const getPlanUpgradeInfo = (currentPlan) => {
        const upgradePath = {
            luna: {
                nextPlan: 'Mercury',
                benefits: [
                    '25 students per period (vs 3)',
                    '1 assignment workspace',
                    'Priority AI processing',
                    'Excel export',
                    'Add-on students: $0.80 each'
                ],
                price: '$19/month'
            },
            free: {
                nextPlan: 'Mercury',
                benefits: [
                    '25 students per period (vs 3)',
                    '1 assignment workspace',
                    'Priority AI processing',
                    'Excel export',
                    'Add-on students: $0.80 each'
                ],
                price: '$19/month'
            },
            starter: {
                nextPlan: 'Mercury',
                benefits: [
                    '25 students per period (vs 3)',
                    '1 assignment workspace',
                    'Priority AI processing',
                    'Excel export',
                    'Add-on students: $0.80 each'
                ],
                price: '$19/month'
            },
            mercury: {
                nextPlan: 'Neptune',
                benefits: [
                    '60 students per period (vs 25)',
                    '2 assignment workspaces (vs 1)',
                    'Priority AI processing',
                    'Excel export',
                    'Advanced analytics',
                    'Add-on: $0.50/student (vs $0.80)'
                ],
                price: '$39/month'
            },
            neptune: {
                nextPlan: 'Jupiter',
                benefits: [
                    '130 students per period (vs 60)',
                    '5 assignment workspaces (vs 2)',
                    'Priority AI processing',
                    'Excel export',
                    'Advanced analytics',
                    'API access',
                    'Add-on: $0.30/student (vs $0.50)'
                ],
                price: '$69/month'
            },
            jupiter: {
                nextPlan: 'Enterprise',
                benefits: [
                    'Unlimited students',
                    'Unlimited workspaces',
                    'Dedicated support',
                    'Custom integrations',
                    'SLA guarantees'
                ],
                price: 'Custom Pricing'
            }
        };

        return upgradePath[currentPlan] || upgradePath.luna;
    };

    const upgradeInfo = getPlanUpgradeInfo(limitInfo?.package || 'luna');

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden transform transition-all border border-zinc-200">
                {/* Header */}
                <div className="bg-zinc-50 border-b border-zinc-200 p-6 relative">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-zinc-400 hover:text-black hover:bg-zinc-100 rounded-lg p-2 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-zinc-100 rounded-xl flex items-center justify-center">
                            <AlertTriangle className="w-6 h-6 text-zinc-700" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-black">Plan Limit Reached</h2>
                            <p className="text-zinc-500 text-sm mt-0.5">
                                You've reached your {limitInfo?.package} plan limit
                            </p>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Current Limit Info */}
                    <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-zinc-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                                <h3 className="font-semibold text-black mb-1 text-sm">Current Limit</h3>
                                <p className="text-sm text-zinc-600">
                                    {limitInfo?.message || 'You have reached your plan limit.'}
                                </p>
                                {limitInfo?.limit && (
                                    <div className="mt-2 text-xs font-mono text-zinc-500">
                                        {limitInfo.current} / {limitInfo.limit} used
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Upgrade Benefits */}
                    <div>
                        <div className="flex items-center gap-2 mb-4">
                            <TrendingUp className="w-4 h-4 text-black" />
                            <h3 className="font-semibold text-black text-sm">
                                Upgrade to {upgradeInfo.nextPlan}
                            </h3>
                        </div>

                        <div className="space-y-2.5">
                            {upgradeInfo.benefits.map((benefit, index) => (
                                <div key={index} className="flex items-start gap-2.5">
                                    <div className="w-5 h-5 bg-zinc-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Check className="w-3 h-3 text-black" />
                                    </div>
                                    <span className="text-sm text-zinc-600">{benefit}</span>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 bg-white rounded-lg p-4 border border-zinc-200">
                            <div className="flex items-center justify-between gap-4">
                                <div>
                                    <div className="text-xs text-zinc-500 uppercase tracking-wide font-medium mb-1">
                                        Starting at
                                    </div>
                                    <div className="text-xl font-bold text-black">{upgradeInfo.price}</div>
                                </div>
                                <button
                                    onClick={() => {
                                        onClose();
                                        onUpgrade?.();
                                    }}
                                    className="bg-black hover:bg-zinc-800 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap"
                                >
                                    <TrendingUp className="w-4 h-4" />
                                    Upgrade Now
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Note */}
                    <div className="text-xs text-zinc-400 text-center pt-4 border-t border-zinc-100">
                        You can upgrade anytime to unlock more capacity and features
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlanLimitModal;
